/* eslint-disable no-continue */
import { RelativePosition } from '@com/components/timeline-panel/utils/track-insert-position'
import { IvNodeUuid, IvSegment, IvTrack, IvTrackIndex } from '../types'
import { getTimelineBySceneUuid } from '../timeline'
import { getSceneSegments } from '.'
import { getGroupIdByTrack } from './track-action'
import { cloneDeep, pick } from 'lodash-es'

type TrackGroupInsertResultFn = () => void
interface TargetTrack extends Pick<IvTrack, 'id' | 'type'> {
  nodes: Array<{
    uuid: IvNodeUuid
    from: IvSegment['groupId'] | undefined
    trackId: IvTrack['id']
  }>
}
export interface TrackGroupInsertResultV2 {
  // 一定会有值的
  to: IvSegment['groupId']
  position: RelativePosition

  targets: Array<TargetTrack>
}

export function useTrackGroupInsertResultsV2(
  sceneUuid: IvSegment['sceneUuid'],
  fn: TrackGroupInsertResultFn,
): TrackGroupInsertResultV2[] {
  const oldTracks = getTimelineBySceneUuid(sceneUuid)
  fn()
  const newTracks = getTimelineBySceneUuid(sceneUuid)

  return useTrackGroupInsertResultsByDiffV2(sceneUuid, newTracks, oldTracks)
}

export function useTrackGroupInsertResultsByDiffV2(
  sceneUuid: IvSegment['sceneUuid'],
  newTracks: IvTrack[],
  oldTracks: IvTrack[],
): TrackGroupInsertResultV2[] {
  const newSegments = getSceneSegments(sceneUuid, newTracks)
  const oldSegments = getSceneSegments(sceneUuid, oldTracks)

  const results: TrackGroupInsertResultV2[] = []
  const findResult = (
    resultLike: Pick<TrackGroupInsertResultV2, 'position' | 'to'>,
  ) =>
    results.find(
      (r) => r.to === resultLike.to && r.position === resultLike.position,
    )
  const pushResult = ({
    newSegment,
    oldSegment,
    targetTrack,
    position,
  }: {
    newSegment: IvSegment
    oldSegment: IvSegment | undefined
    targetTrack: IvTrack
    position: TrackGroupInsertResultV2['position']
  }): void => {
    // to没有值情况：targetTrack 所依赖的groupId并没有完成回写
    const to: TrackGroupInsertResultV2['to'] =
      newSegment.groupId || (targetTrack ? getGroupIdByTrack(targetTrack) : '')

    const node: TargetTrack['nodes'][number] = {
      uuid: newSegment.id,
      from: oldSegment?.groupId,
      trackId: newSegment.trackId,
    }
    const track: TargetTrack = {
      ...pick(targetTrack, ['id', 'type']),
      nodes: [node],
    }

    const push = (targetResult: TrackGroupInsertResultV2) => {
      const targetResultTrack = targetResult.targets.find(
        (t) => t.id === targetTrack.id,
      )

      if (targetResultTrack) {
        targetResultTrack.nodes.push(node)
      } else {
        targetResult.targets.push(track)
      }
    }

    if (to) {
      const targetResult = findResult({ position, to })
      if (targetResult) {
        push(targetResult)
      } else {
        results.push({
          to,
          position,
          targets: [track],
        })
      }
    } else {
      const targetResult = results.find(
        (r) =>
          r.position === position &&
          r.targets.some((t) =>
            t.nodes.some((node) => node.trackId === targetTrack.id),
          ),
      )

      if (targetResult) {
        push(targetResult)
      } else {
        console.log('aaa handle it when nexttick.', newSegment)
        // handle it when nexttick.
        newSegments.push(newSegment)
      }
    }
  }

  let _newSegment: IvSegment | undefined
  while ((_newSegment = newSegments.shift())) {
    const newSegment = _newSegment
    const targetOldSegment = oldSegments.find((s) => s.id === newSegment.id)
    const newSegmentTrackIndex = newTracks.findIndex(
      (t) => t.id === newSegment.trackId,
    )
    const newSegmentTrack = newTracks[newSegmentTrackIndex]

    if (newSegmentTrackIndex === -1 || !newSegmentTrack) {
      // 未处理以下cases：
      // 1. 新目标轨道没找到
      // 2. 新目标轨道没有groupId的相对值
      console.error(
        `useTrackGroupInsertResultsByDiff 新目标轨道没找到`,
        newSegment.trackId,
        cloneDeep(newSegment),
        cloneDeep(newTracks),
      )

      continue
    }

    const _push = (
      targetTrack: IvTrack,
      position: TrackGroupInsertResultV2['position'],
    ) =>
      pushResult({
        newSegment,
        oldSegment: targetOldSegment,
        targetTrack,
        position,
      })

    if (newSegment.groupId) {
      const isSomeGroupId =
        targetOldSegment && targetOldSegment.groupId === newSegment.groupId

      // 仅处理位置变了的。位置没变化的，不处理
      if (!isSomeGroupId) {
        _push(newSegmentTrack, 'middle')
      }

      continue
    } else if (newSegmentTrackIndex > -1) {
      const aboveTrack = findHasSegmentTrack({
        sceneUuid: newSegment.sceneUuid,
        startIndex: newSegmentTrackIndex,
        step: -1,
        tracks: newTracks,
      })
      if (aboveTrack) {
        _push(aboveTrack, 'bottom')
        continue
      }

      const belowTrack = findHasSegmentTrack({
        sceneUuid: newSegment.sceneUuid,
        startIndex: newSegmentTrackIndex,
        step: 1,
        tracks: newTracks,
      })
      if (belowTrack) {
        _push(belowTrack, 'top')
        continue
      }

      console.error(
        `useTrackGroupInsertResultsByDiff 没有找到新轨道的相对groupId node`,
        cloneDeep(newSegmentTrack),
      )
    }
  }

  return results
}
interface IgnoreSegmentIdsOptions {
  ignoreSegmentIds?: IvSegment['id'][]
}

interface FindHasSegmentTrackOptions extends IgnoreSegmentIdsOptions {
  sceneUuid: IvSegment['sceneUuid']
  startIndex: IvTrackIndex
  step: 1 | -1
  tracks: IvTrack[]
}

// eslint-disable-next-line consistent-return
export function findHasSegmentTrack(
  options: FindHasSegmentTrackOptions,
): IvTrack | undefined {
  const { sceneUuid, startIndex, step, tracks, ignoreSegmentIds } = options

  let index = startIndex + step
  while (tracks[index]) {
    const t = tracks[index]

    if (
      t &&
      hasSegmentTrack(sceneUuid, t, {
        ignoreSegmentIds,
      })
    ) {
      return t
    }

    index += step
  }
}

export function hasSegmentTrack(
  sceneUuid: IvSegment['sceneUuid'],
  t: IvTrack,
  options?: IgnoreSegmentIdsOptions,
): boolean {
  const { ignoreSegmentIds = [] } = options ?? {}
  return Boolean(
    t.segments.filter(
      (s) => s.sceneUuid === sceneUuid && !ignoreSegmentIds.includes(s.id),
    ).length,
  )
}
